/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type SimpleChanges, Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import {
    type ColumnDef, type Rest, type TableConfig, ButtonService, CAPACITY_UNIT, TableAbstract,
    UNPLACEHOLDER, UtilService,
} from '@dm/core';
import { type Button } from '@dm/core/types';
import { ToolService } from 'app/common/services';
import { type Dtree, type FileSystem, type Quota } from 'app/common/types/mo';
import { ProductConfig } from 'app/config';
import { isUndefined } from 'lodash';

import { ModifyUserQuotaComponent } from '../modify-user-quota/modify-user-quota.component';
import { UserQuotaStrService } from '../user-quota-str.service';
import { CreateFrontTableUserQuotaComponent } from './create-front-table-user-quota/create-front-table-user-quota.component';

@Component({
    selector: 'app-front-table-user-quota',
    templateUrl: './front-table-user-quota.component.html',
    styleUrls: ['./front-table-user-quota.component.scss'],
})
export class FrontTableUserQuotaComponent extends TableAbstract<Quota> implements OnChanges {
    @Input() tableData: Quota[] = [];
    // tableData的双向绑定实现，每一次改变值，都要emit出去
    @Output() tableDataChange: EventEmitter<any> = new EventEmitter();

    @Input() fileSystem?: FileSystem;
    /**
     * 传入的dtree,传了dtree必须传fileSystem
     */
    @Input() dtree?: Dtree;
    @Input() vstoreId: string;
    @Input() vstoreName: string;


    i18n = this.str.i18n;

    vstoreColumnIndex = 1;

    tableRest: Rest;
    tableConfig: TableConfig;
    tableColumnDefs: ColumnDef[];
    selection: Quota[] = [];

    createBtn: Button;
    modifyBtn: Button;
    deleteBtn: Button;

    /**
     * 是否隐藏左边文件系统表格
     */
    leftHide = false;

    constructor(
        public str: UserQuotaStrService,
        private buttonService: ButtonService,
        injector: Injector,
        private utilService: UtilService,
        private toolService: ToolService,
    ) {
        super(injector);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.vstoreName && this.createBtn) {
            this.createBtn.disabled = !this.vstoreName;
            this.tableData = [];
            this.updateTableData();
        }
    }

    initBtns(): void {
        this.createBtn = this.buttonService.create({
            id: 'create-front-table-user-quota',
            label: this.i18n.createBtn,
            theme: 'primary',
            permission: ['quota_W'],
            disabled: !this.vstoreName,
            disabledTips: this.i18n.selectVstore,
            command: () => {
                this.frontCreate();
            },
        });

        this.modifyBtn = this.buttonService.create({
            id: 'modify-front-table-user-quota',
            label: this.i18n.modifyBtn,
            permission: ['quota_W'],
            command: ({ data }) => {
                this.modify(data);
            },
        });

        this.deleteBtn = this.buttonService.create({
            id: 'delete-front-table-user-quota',
            label: this.i18n.deleteBtn,
            permission: ['quota_W'],
            disabledCheck: (data) => !data && this.selection.length === 0,
            command: ({ data }) => {
                this.delete(data);
            },
        });
    }

    initTable(): void {
        this.tableConfig = {
            dataKey: 'ID',
            selectionMode: 'checkbox',
            pollingPeriod: 15,
            menuItems: [
                this.modifyBtn,
                this.deleteBtn,
            ],
        };

        this.tableColumnDefs = [
            {
                field: 'ID',
                header: this.i18n.idLab,
                hidden: true,
                columnFilter: false,
            },
            {
                field: 'RESUSENAME',
                header: this.i18n.dtreeLab,
                filterTrim: false,
                ignore: ProductConfig.isHideDtree,
                render: this.str.resUseNameRender,
                className: ['table-cell-text-pre'],
            },
            {
                field: 'QUOTATYPE',
                header: this.i18n.quotaTypeLab,
                columnFilter: true,
                // 业务暂不支持，待支持后放开
                // filterType: 'dropdown',
                // options: this.str.quotaTypeOpts,
                render: (value: string) => this.str.quotaTypeI18n[value],
            },
            {
                field: 'USRGRPOWNERNAME',
                header: this.i18n.userLab,
                columnFilter: true,
                render: this.str.userRender,
                className: ['table-cell-text-pre'],
            },
            {
                field: 'USRGRPOWNERNAME',
                header: this.i18n.userGroupLab,
                columnFilter: true,
                render: this.str.groupRender,
                className: ['table-cell-text-pre'],
            },
            {
                field: 'USRGRPTYPE',
                header: this.i18n.userOrGroupTypeLab,
                columnFilter: true,
                render: this.str.userOrGroupTypeRender,
            },
            {
                field: 'SPACEHARDQUOTA',
                header: this.i18n.spaceQuotaSoftHardLab,
                align: 'right',
                columnFilter: true,
                width: '210px',
                render: (vale: string, col, data: Quota): string => {
                    // 软 / 硬
                    const hard = (isUndefined(data.SPACEHARDQUOTA) || this.toolService.isInvalidNum(data.SPACEHARDQUOTA, true)) ?
                        UNPLACEHOLDER : this.utilService.adaptUnit(data.SPACEHARDQUOTA, CAPACITY_UNIT.KB).label;

                    const soft = (isUndefined(data.SPACESOFTQUOTA) || this.toolService.isInvalidNum(data.SPACESOFTQUOTA, true)) ?
                        UNPLACEHOLDER : this.utilService.adaptUnit(data.SPACESOFTQUOTA, CAPACITY_UNIT.KB).label;

                    return soft + '/' + hard;
                },
            },
            {
                field: 'FILEHARDQUOTA',
                header: this.i18n.fileQuotaSoftHardLab,
                align: 'right',
                columnFilter: true,
                width: '150px',
                render: (vale: string, col, data: Quota): string => {
                    // 软 / 硬
                    const hard = (isUndefined(data.FILEHARDQUOTA) || (this.toolService.isInvalidNum(data.FILEHARDQUOTA, true)) ?
                        UNPLACEHOLDER : data.FILEHARDQUOTA);

                    const soft = (isUndefined(data.FILESOFTQUOTA) || (this.toolService.isInvalidNum(data.FILESOFTQUOTA, true)) ?
                        UNPLACEHOLDER : data.FILESOFTQUOTA);

                    return soft + '/' + hard;
                },
            },
        ];
    }

    updateTableData(forceDraw = false): void {
        this.tableDataChange.emit(this.tableData);
        this.dt.updateData(this.tableData);
        // 用于解决修改的时候，硬配额没变，只变了软配额，不刷新的表格的场景
        if (forceDraw) {
            this.dt.redraw();
        }
    }

    /**
     * 构造ID
     */
    formatID(datas: Quota[]): Quota[] {
        const key = '^_&_^';
        datas.forEach((data) => {
            if (!data.ID) {
                data.ID = data.PARENTTYPE + key + data.QUOTATYPE + key + data.DOMAINTYPE +
                    key + data.USRGRPTYPE + key + data.NAMETYPE + key + data.USRGRPOWNERNAME;
            }
        });
        return datas;
    }

    frontCreate(): void {
        this.doAction(CreateFrontTableUserQuotaComponent, {
            fileSystem: this.fileSystem,
            vstoreId: this.vstoreId,
            vstoreName: this.vstoreName,
            dtree: this.dtree || null,
            onClose: ({ data }: { data: Quota[] }) => {
                if (!data) {
                    return;
                }
                const datas = this.formatID(data);
                // 非重复数据
                const tempDatas: Quota[] = [];

                // 遍历待添加的数据，过滤出重复ID的和非重复ID的
                datas.forEach((item) => {
                    const find_oneTableData = this.tableData.find((oneTableData) => {
                        if (oneTableData.ID === item.ID) {
                            oneTableData.PARENTTYPE = item.PARENTTYPE;
                            oneTableData.QUOTATYPE = item.QUOTATYPE;
                            oneTableData.DOMAINTYPE = item.DOMAINTYPE;
                            oneTableData.USRGRPTYPE = item.USRGRPTYPE;
                            oneTableData.NAMETYPE = item.NAMETYPE;
                            oneTableData.USRGRPOWNERNAME = item.USRGRPOWNERNAME;
                            oneTableData.SPACEUNITTYPE = item.SPACEUNITTYPE;
                            oneTableData.FILEHARDQUOTA = item.FILEHARDQUOTA;
                            oneTableData.FILESOFTQUOTA = item.FILESOFTQUOTA;
                            oneTableData.SPACEHARDQUOTA = item.SPACEHARDQUOTA;
                            oneTableData.SPACESOFTQUOTA = item.SPACESOFTQUOTA;
                            return true;
                        }

                        return undefined as boolean;
                    });

                    if (!find_oneTableData) {
                        tempDatas.push(item);
                    }
                });
                // 拿到数据之后构造一下ID
                // 如果ID有重复的，就覆盖数据
                this.tableData.push(...tempDatas);
                this.updateTableData();
            },
        });
    }

    modify(quotaData: Quota): void {
        this.doAction(ModifyUserQuotaComponent, {
            data: quotaData,
            isFrontTable: true,
            onClose: ({ data }: { data: Quota }): void => {
                if (!data) {
                    return;
                }
                this.tableData.find((oneTableData): boolean => {
                    if (oneTableData.ID === data.ID) {
                        // 修改只支持修改值
                        // 修改这里要特殊处理下，如果是0的话，就变为undefined
                        oneTableData.SPACEUNITTYPE = Number(data.SPACEUNITTYPE) === 0 ? undefined : data.SPACEUNITTYPE;
                        oneTableData.FILEHARDQUOTA = Number(data.FILEHARDQUOTA) === 0 ? undefined : data.FILEHARDQUOTA;
                        oneTableData.FILESOFTQUOTA = Number(data.FILESOFTQUOTA) === 0 ? undefined : data.FILESOFTQUOTA;
                        oneTableData.SPACEHARDQUOTA = Number(data.SPACEHARDQUOTA) === 0 ? undefined : data.SPACEHARDQUOTA;
                        oneTableData.SPACESOFTQUOTA = Number(data.SPACESOFTQUOTA) === 0 ? undefined : data.SPACESOFTQUOTA;
                        return true;
                    }

                    return undefined as boolean;
                });
                this.updateTableData(true);
            },
        });
    }

    /**
     * 前端删除
     */
    delete(data?: Quota): void {
        const removeList = data ? [data] : this.selection;
        this.tableData = this.tableData.filter((tableData) => {
            // 如果这个data的名字 在removeList中，那就得删掉
            const tempIndex = removeList.findIndex((removeItem) => removeItem.ID === tableData.ID);
            // filter true的留下， false的删掉
            return tempIndex === -1;
        });

        // 更改数据后，刷新表格，触发双向绑定
        this.updateTableData();
    }
}
